/**
 * Special node rendering for the diff engine.
 *
 * Maps node types to renderer functions. Each renderer knows HOW to
 * render the HTML structure; data resolution is delegated to the runtime.
 *
 * IMPORTANT: This file must have ZERO product imports - only engine imports.
 * It is part of the shared engine package used by both frontend and backend.
 */
import type { Element } from 'domhandler';

import { HEADING_TAGS, getElementId, ID_ATTR, isNodeLoaded } from '../schema';
import { marksEqual } from '../core/compare';
import { serializeElement } from '../parser/parser';
import { escapeHtml } from '../renderer/html';
import type { TDiffNode } from '../types';
import type { RenderSpecialNodeHelpers } from '../renderer/renderDiffTree';
import type { TDiffSide, TDocDiffRuntime, TDocDiffOptions } from './types';
import { formatReferenceHtml, buildAttrs } from './formatReference';
import { renderContent, type TContentHooks } from '../content/renderContent';
import { normalizeInlineBlockValue } from '../content/helpers';
import {
  REFERENCE_LOAD_ERROR,
  INLINE_BLOCK_LOAD_ERROR,
  INLINE_BLOCK_NOT_LOADED,
  BLOCK_LOAD_ERROR,
  BLOCK_NOT_LOADED,
  wrapStaticBlock,
} from '../content/constants';
import { formatCitationLabel, parseIndexAttr } from '../text';

// Prevents infinite recursion in block-in-block content. Independent from
// renderContent's MAX_CONTENT_DEPTH - block expansion resets to depth=0
// so refs/captions still resolve inside expanded blocks at the diff boundary.
const MAX_TREE_DIFF_DEPTH = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getStringAttrs = (node: TDiffNode, side: 'current' | 'previous'): Record<string, string> => {
  const source = side === 'current' ? node.attrs : (node.prevAttrs ?? node.attrs);
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === 'string') result[key] = value;
  }
  return result;
};

const getSourceElement = (node: TDiffNode, side: 'current' | 'previous'): Element | undefined =>
  (side === 'current' ? node.currElement : node.prevElement) ?? undefined;

const getActiveElement = (node: TDiffNode): Element | undefined => node.currElement ?? node.prevElement ?? undefined;

const getNodeId = (node: TDiffNode): string => {
  const el = getActiveElement(node);
  return el ? (getElementId(el) ?? '') : '';
};

// ---------------------------------------------------------------------------
// Node renderers
// ---------------------------------------------------------------------------

const renderHeadingNode = async (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  runtime: TDocDiffRuntime,
  opts: TDocDiffOptions
): Promise<string | null> => {
  const activeEl = getActiveElement(node);
  if (!activeEl) return null;

  const tag = activeEl.tagName;
  const childrenHtml = await helpers.renderChildren();
  const headingId = getNodeId(node);
  const headingPair = runtime.resolveHeading(headingId);
  const currentHeading = node.currElement ? headingPair.current : undefined;
  const previousHeading = node.prevElement ? headingPair.previous : undefined;

  let content = `<span class="heading-text">${childrenHtml}</span>`;
  const currentIsH5Ref = currentHeading?.level === 5 && opts.isH5RefEnabled;

  if (currentIsH5Ref) {
    content = `<span class="heading-text h5-ref">${childrenHtml}</span>`;
  } else {
    // Show normal numbering even if previous was H5 - the tag change (h5→h4)
    // already communicates the level transition visually.
    const currentNumbering = currentHeading?.numbering;
    const previousNumbering = previousHeading?.numbering;
    let numberingHtml = '';

    if (node.change === 'inserted') {
      if (currentNumbering) numberingHtml = `<span class="heading-label">${escapeHtml(currentNumbering)}</span>`;
    } else if (node.change === 'deleted') {
      if (previousNumbering) numberingHtml = `<span class="heading-label">${escapeHtml(previousNumbering)}</span>`;
    } else if (currentNumbering || previousNumbering) {
      const levelChanged =
        node.prevElement && node.currElement && node.prevElement.tagName !== node.currElement.tagName;

      if (levelChanged && currentNumbering !== previousNumbering) {
        // Level changed (h1→h2) - numbering label communicates the structural change.
        numberingHtml = `<span class="heading-label">${
          previousNumbering ? `<del>${escapeHtml(previousNumbering)}</del>` : ''
        }${currentNumbering ? `<ins>${escapeHtml(currentNumbering)}</ins>` : ''}</span>`;
      } else {
        // No level change - show current numbering, suppress renumbering noise.
        const numbering = currentNumbering;
        numberingHtml = numbering ? `<span class="heading-label">${escapeHtml(numbering)}</span>` : '';
      }
    }

    if (numberingHtml) {
      content = `${numberingHtml}<span class="heading-text">${childrenHtml}</span>`;
    }
  }

  // Add id for anchor navigation (non-deleted only to avoid duplicate IDs)
  const idAttr = node.change !== 'deleted' ? ` id="${escapeHtml(headingId)}"` : '';
  const htmlString = `<${tag}${idAttr}>${content}</${tag}>`;
  if (node.change === 'inserted') return helpers.wrapInserted(htmlString);
  if (node.change === 'deleted') return helpers.wrapDeleted(htmlString);
  return htmlString;
};

const renderCaptionNode = async (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  runtime: TDocDiffRuntime
): Promise<string | null> => {
  const activeEl = getActiveElement(node);
  if (!activeEl) return null;

  const id = getNodeId(node);
  const align = node.currElement?.attribs['data-align'] || node.prevElement?.attribs['data-align'] || 'left';
  const bookmarkKind =
    node.currElement?.attribs['data-bookmark-kind'] || node.prevElement?.attribs['data-bookmark-kind'] || '';

  // Extract per-side attributes for prefix computation.
  const currKind = node.currElement?.attribs['data-bookmark-kind'] || '';
  const currIndex = node.currElement?.attribs['data-index'] || '';
  const prevKind = node.prevElement?.attribs['data-bookmark-kind'] || '';
  const prevIndex = node.prevElement?.attribs['data-index'] || '';

  // Resolve the label (e.g. "Figure", "Table") separately from the index.
  const currentLabel = node.currElement
    ? runtime.resolveCaptionPrefix(currKind, '', 'current').replace(/\s*:?\s*$/, '')
    : '';
  const previousLabel = node.prevElement
    ? runtime.resolveCaptionPrefix(prevKind, '', 'previous').replace(/\s*:?\s*$/, '')
    : '';
  const label = currentLabel || previousLabel;

  // Always show current index (or previous for deleted captions).
  // Index changes from reordering (inserting figure above) are noise.
  const index = node.change === 'deleted' ? prevIndex : currIndex;
  const indexHtml = escapeHtml(index);

  const prefixHtml = `${escapeHtml(label)} ${indexHtml}:`;

  const contentHtml = await helpers.renderChildren();
  // Add id for anchor navigation (non-deleted only to avoid duplicate IDs)
  const captionIdAttr = node.change !== 'deleted' ? id : undefined;
  // Use the appropriate side's index for the wrapper attribute:
  // current for inserted/modified, previous for deleted captions.
  const wrapperIndex = node.change === 'deleted' ? prevIndex : currIndex || prevIndex;
  const captionAttrs = buildAttrs({
    id: captionIdAttr,
    class: 'caption-wrapper',
    'data-align': align,
    'data-bookmark-kind': bookmarkKind,
    'data-index': wrapperIndex,
    [ID_ATTR]: id,
  });
  const htmlString = `<span${captionAttrs}><span class="caption-prefix">${prefixHtml}</span> <span class="caption-content">${contentHtml}</span></span>`;

  if (node.change === 'inserted') return helpers.wrapInserted(htmlString);
  if (node.change === 'deleted') return helpers.wrapDeleted(htmlString);
  return htmlString;
};

const renderReferenceNode = async (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  runtime: TDocDiffRuntime,
  opts: TDocDiffOptions
): Promise<string | null> => {
  // prevMarks is undefined when marks are unchanged - fall back to current marks, not [].
  const nodeMarksChanged = !marksEqual(node.marks, node.prevMarks ?? node.marks);
  const currentAttrs = node.currElement ? getStringAttrs(node, 'current') : undefined;
  const previousAttrs = node.prevElement ? getStringAttrs(node, 'previous') : undefined;

  const resolveAndFormat = (attrs: Record<string, string>, side: 'current' | 'previous'): string => {
    const data = runtime.resolveReference(attrs, side);
    if (!data)
      return `<span${buildAttrs({ [ID_ATTR]: attrs[ID_ATTR] ?? '' })} class="ref-error">${escapeHtml(REFERENCE_LOAD_ERROR)}</span>`;
    return formatReferenceHtml(data, attrs, opts);
  };

  if (node.change === 'inserted' && currentAttrs) {
    return helpers.wrapInserted(helpers.wrapCurrentMarks(resolveAndFormat(currentAttrs, 'current')));
  }

  if (node.change === 'deleted' && previousAttrs) {
    return helpers.wrapDeleted(helpers.wrapPreviousMarks(resolveAndFormat(previousAttrs, 'previous')));
  }

  if (currentAttrs && previousAttrs) {
    // References auto-resolve - users can't manually update them.
    // When the ref element itself hasn't changed, the resolved text change
    // is automatic (e.g. target renumbered). Suppress these cascading diffs.
    if (node.change === 'unchanged' && !nodeMarksChanged) {
      return helpers.wrapCurrentMarks(resolveAndFormat(currentAttrs, 'current'));
    }

    const currentHtml = resolveAndFormat(currentAttrs, 'current');
    const previousHtml = resolveAndFormat(previousAttrs, 'previous');

    if (nodeMarksChanged) {
      return `${helpers.wrapDeleted(helpers.wrapPreviousMarks(previousHtml))}${helpers.wrapInserted(
        helpers.wrapCurrentMarks(currentHtml)
      )}`;
    }

    if (currentHtml === previousHtml) {
      return helpers.wrapCurrentMarks(currentHtml);
    }

    return `${helpers.wrapDeleted(helpers.wrapPreviousMarks(previousHtml))}${helpers.wrapInserted(
      helpers.wrapCurrentMarks(currentHtml)
    )}`;
  }

  return null;
};

/** The inserted / deleted / modified handling every atom renderer needs, in one place. */
const wrapByChange = (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  currentHtml: string,
  previousHtml: string
): string => {
  if (node.change === 'inserted') return helpers.wrapInserted(currentHtml);
  if (node.change === 'deleted') return helpers.wrapDeleted(previousHtml);
  if (node.change === 'modified' && currentHtml && previousHtml) {
    return `${helpers.wrapDeleted(previousHtml)}${helpers.wrapInserted(currentHtml)}`;
  }
  return currentHtml || previousHtml;
};

const renderImageNode = async (node: TDiffNode, helpers: RenderSpecialNodeHelpers): Promise<string | null> => {
  const renderImage = (el?: Element) => {
    if (!el) return '';
    return serializeElement(el);
  };

  const currentHtml = renderImage(getSourceElement(node, 'current'));
  const previousHtml = renderImage(getSourceElement(node, 'previous'));

  return wrapByChange(node, helpers, currentHtml, previousHtml);
};

const renderBlockNode = async (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  runtime: TDocDiffRuntime,
  opts: TDocDiffOptions,
  depth: number,
  recursiveDiff: (prevHtml: string, currHtml: string) => Promise<string>,
  hooks?: TContentHooks
): Promise<string | null> => {
  if (!getActiveElement(node)) return null;

  const blockId = getNodeId(node);
  const inline = node.type === 'inline-block';
  const nodeMarksChanged = !marksEqual(node.marks, node.prevMarks ?? node.marks);

  const currentAttrs = node.currElement ? getStringAttrs(node, 'current') : undefined;
  const previousAttrs = node.prevElement ? getStringAttrs(node, 'previous') : undefined;

  // A block whose registration has not settled cannot be resolved, and asking the runtime for it
  // reports a load failure for something that is not broken. The editor shows a placeholder instead.
  const nodeLoaded = (attrs: Readonly<Record<string, string>>, side: TDiffSide) =>
    runtime.isNodeLoaded?.(attrs, side) ?? isNodeLoaded(attrs);
  const currentLoaded = !!currentAttrs && nodeLoaded(currentAttrs, 'current');
  const previousLoaded = !!previousAttrs && nodeLoaded(previousAttrs, 'previous');
  const currentInfo = currentLoaded ? await runtime.getBlockInfo(blockId, currentAttrs!, 'current') : undefined;
  const previousInfo = previousLoaded ? await runtime.getBlockInfo(blockId, previousAttrs!, 'previous') : undefined;

  // --- Inline blocks ---

  if (inline) {
    const notLoadedYet = (attrs?: Readonly<Record<string, string>>, side: TDiffSide = 'current') =>
      attrs && !nodeLoaded(attrs, side);
    const currentValue = normalizeInlineBlockValue(
      currentInfo?.html || (notLoadedYet(currentAttrs) ? INLINE_BLOCK_NOT_LOADED : INLINE_BLOCK_LOAD_ERROR)
    );
    const previousValue = normalizeInlineBlockValue(
      previousInfo?.html ||
        (notLoadedYet(previousAttrs, 'previous') ? INLINE_BLOCK_NOT_LOADED : INLINE_BLOCK_LOAD_ERROR)
    );
    const unresolvable = notLoadedYet(currentAttrs) || notLoadedYet(previousAttrs, 'previous');
    const inlineClass = currentInfo || previousInfo || unresolvable ? '' : 'inline-block-error';
    const className = ['inline-block-styled', inlineClass].filter(Boolean).join(' ');
    const currentHtml = helpers.wrapCurrentMarks(`<span class="${className}">${escapeHtml(currentValue)}</span>`);
    const previousHtml = helpers.wrapPreviousMarks(`<span class="${className}">${escapeHtml(previousValue)}</span>`);

    if (node.change === 'inserted') return helpers.wrapInserted(currentHtml);
    if (node.change === 'deleted') return helpers.wrapDeleted(previousHtml);
    if (!currentInfo) return currentHtml;
    if (!previousInfo || previousInfo.versionId === currentInfo.versionId) {
      if (nodeMarksChanged && previousInfo) {
        return `${helpers.wrapDeleted(previousHtml)}${helpers.wrapInserted(currentHtml)}`;
      }
      return currentHtml;
    }
    return `${helpers.wrapDeleted(previousHtml)}${helpers.wrapInserted(currentHtml)}`;
  }

  // --- Reusable blocks ---

  const renderCurrentBlock = async () => {
    if (!currentLoaded) return `<span>${escapeHtml(BLOCK_NOT_LOADED)}</span>`;
    if (!currentInfo) return `<span class="block-error">${escapeHtml(BLOCK_LOAD_ERROR)}</span>`;
    if (depth >= MAX_TREE_DIFF_DEPTH) return currentInfo.html;
    return renderContent(currentInfo.html, runtime, opts, 'current', hooks);
  };

  const renderPreviousBlock = async () => {
    if (!previousLoaded) return `<span>${escapeHtml(BLOCK_NOT_LOADED)}</span>`;
    if (!previousInfo) return `<span class="block-error">${escapeHtml(BLOCK_LOAD_ERROR)}</span>`;
    if (depth >= MAX_TREE_DIFF_DEPTH) return previousInfo.html;
    return renderContent(previousInfo.html, runtime, opts, 'previous', hooks);
  };

  const header = (side: 'current' | 'previous') => {
    const attrs = side === 'current' ? currentAttrs : previousAttrs;
    return attrs ? (runtime.renderBlockHeader?.(attrs, side) ?? '') : '';
  };
  const wrapBlock = (content: string, side: 'current' | 'previous' = 'current') =>
    wrapStaticBlock(header(side), content);

  if (node.change === 'inserted') {
    return helpers.wrapInserted(wrapBlock(await renderCurrentBlock()));
  }
  if (node.change === 'deleted') {
    return helpers.wrapDeleted(wrapBlock(await renderPreviousBlock(), 'previous'));
  }

  // No current info or versions match - show current only
  if (!currentInfo || !previousInfo || previousInfo.versionId === currentInfo.versionId) {
    return wrapBlock(await renderCurrentBlock());
  }

  // Depth limit - show side-by-side without recursive diff
  if (depth >= MAX_TREE_DIFF_DEPTH) {
    return `${helpers.wrapDeleted(wrapBlock(await renderPreviousBlock(), 'previous'))}${helpers.wrapInserted(
      wrapBlock(await renderCurrentBlock())
    )}`;
  }

  // Recursive diff of block content
  const nestedDiff = await recursiveDiff(previousInfo.html, currentInfo.html);
  return wrapBlock(nestedDiff);
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Creates the `renderSpecialNode` callback for the diff renderer.
 *
 * The returned function is passed as `options.renderSpecialNode` to
 * `diffAndRenderTree`. It intercepts product-specific node types
 * and delegates data resolution to the runtime.
 *
 * `recursiveDiff` is a callback for nested block diffing - avoids
 * circular imports by deferring the `diffHtmlTree` call to the caller.
 */
/**
 * The stored element is empty - the editor's node view draws the `[n]` chip - so anything rendering
 * outside ProseMirror has to draw it here or the citation vanishes from the output entirely.
 */
const renderLiteratureCitationNode = async (
  node: TDiffNode,
  helpers: RenderSpecialNodeHelpers,
  runtime: TDocDiffRuntime
): Promise<string | null> => {
  const renderChip = (el?: Element) => {
    if (!el) return '';
    // Content already present means something upstream drew the label; do not double it.
    if (el.children?.length) return serializeElement(el);
    const { class: existing, ...rest } = el.attribs ?? {};
    const attrs = buildAttrs({ ...rest, class: [existing, 'literature-citation'].filter(Boolean).join(' ') });
    const literatureId = el.attribs?.['data-literature-id'];
    const index =
      parseIndexAttr(el.attribs?.['data-index']) ??
      (literatureId ? (runtime.resolveLiteratureCitationIndex?.(literatureId) ?? null) : null);
    return `<literature-citation${attrs}>${formatCitationLabel(index)}</literature-citation>`;
  };

  const currentHtml = renderChip(getSourceElement(node, 'current'));
  const previousHtml = renderChip(getSourceElement(node, 'previous'));

  return wrapByChange(node, helpers, currentHtml, previousHtml);
};

export const createRenderSpecialNode =
  (
    runtime: TDocDiffRuntime,
    opts: TDocDiffOptions,
    depth: number,
    recursiveDiff: (prevHtml: string, currHtml: string) => Promise<string>,
    hooks?: TContentHooks
  ): ((node: TDiffNode, helpers: RenderSpecialNodeHelpers) => Promise<string | null>) =>
  async (node, helpers) => {
    if (HEADING_TAGS.has(node.type)) return renderHeadingNode(node, helpers, runtime, opts);
    if (node.type === 'caption-node') return renderCaptionNode(node, helpers, runtime);
    if (node.type === 'ref') return renderReferenceNode(node, helpers, runtime, opts);
    if (node.type === 'img') return renderImageNode(node, helpers);
    if (node.type === 'literature-citation') return renderLiteratureCitationNode(node, helpers, runtime);
    if (node.type === 'inline-block' || node.type === 'reusable-block') {
      return renderBlockNode(node, helpers, runtime, opts, depth, recursiveDiff, hooks);
    }
    return null;
  };
